import React, { useEffect, useRef, useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import axiosInstance from "./api/axiosInstance";
import "./GoogleAddressSearch.css";

function AddressSuggestions({ suggestions, loading, onSelect }) {
  if (loading) {
    return <div className="gas-suggestions-empty">Searching…</div>;
  }
  if (!suggestions.length) return null;
  return (
    <ul className="gas-suggestions" role="listbox">
      {suggestions.map((item) => (
        <li key={item.place_id}>
          <button type="button" onClick={() => onSelect(item)}>
            {item.description}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * DigiX Google Maps address search.
 * Fills structured address fields from Places (search only — fields are read-only).
 *
 * onChange(next) receives:
 * { addressLine1, addressLine2, area, city, state, country, pincode, formattedAddress }
 */
function GoogleAddressSearch({ value = {}, onChange, required = false }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const timeoutRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const emitEmpty = () => {
    onChange?.({
      addressLine1: "",
      addressLine2: "",
      area: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      formattedAddress: "",
    });
  };

  const search = (text) => {
    setQuery(text);
    setError("");
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const trimmed = text.trim();
    if (trimmed.length < 3) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    timeoutRef.current = setTimeout(async () => {
      try {
        const res = await axiosInstance.get("/get-address", {
          params: { address: trimmed },
        });
        const rows = Array.isArray(res.data?.suggestions) ? res.data.suggestions : [];
        setSuggestions(rows);
        setOpen(rows.length > 0);
      } catch (err) {
        setSuggestions([]);
        setOpen(false);
        setError(err.response?.data?.error || err.response?.data?.message || "Address search failed.");
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const selectSuggestion = async (suggestion) => {
    setError("");
    setQuery("");
    setSuggestions([]);
    setOpen(false);
    setResolving(true);
    try {
      const res = await axiosInstance.get("/get-address-details", {
        params: { placeId: suggestion.place_id },
      });
      const details = res.data || {};
      if (details.error) {
        setError(details.error);
        return;
      }
      onChange?.({
        addressLine1: details.addressLine1 || "",
        addressLine2: details.addressLine2 || "",
        area: details.area || "",
        city: details.city || "",
        state: details.state || "",
        country: details.country || "",
        pincode: details.postalCode || "",
        formattedAddress: details.formattedAddress || suggestion.description || "",
      });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Could not load address details.");
    } finally {
      setResolving(false);
    }
  };

  const clearAddress = () => {
    setQuery("");
    setSuggestions([]);
    setOpen(false);
    setError("");
    emitEmpty();
  };

  const hasStructured =
    value.addressLine1 ||
    value.addressLine2 ||
    value.area ||
    value.city ||
    value.state ||
    value.country ||
    value.pincode;

  return (
    <div className="gas-root">
      <div className="form-group full-width">
        <label>
          Search address (Google Maps)
          {required ? <span className="required"> *</span> : null}
        </label>
        <div className="gas-search" ref={wrapRef}>
          <FaSearch className="gas-search-icon" aria-hidden />
          <input
            type="text"
            value={query}
            onChange={(e) => search(e.target.value)}
            onFocus={() => suggestions.length && setOpen(true)}
            placeholder="Search Google Maps address…"
            autoComplete="off"
            disabled={resolving}
          />
          {(query || hasStructured) && (
            <button type="button" className="gas-clear" onClick={clearAddress} aria-label="Clear address">
              <FaTimes />
            </button>
          )}
          {open ? (
            <AddressSuggestions
              suggestions={suggestions}
              loading={loading}
              onSelect={selectSuggestion}
            />
          ) : null}
        </div>
        {resolving ? <p className="gas-hint">Filling address fields…</p> : null}
        {error ? <p className="gas-error">{error}</p> : null}
        {required && !hasStructured ? (
          <p className="gas-hint">Select an address from Google Maps suggestions.</p>
        ) : null}
      </div>

      <div className="form-group">
        <label>Address Line 1</label>
        <input type="text" value={value.addressLine1 || ""} readOnly />
      </div>
      <div className="form-group">
        <label>Address Line 2</label>
        <input type="text" value={value.addressLine2 || ""} readOnly />
      </div>
      <div className="form-group">
        <label>Area</label>
        <input type="text" value={value.area || ""} readOnly />
      </div>
      <div className="form-group">
        <label>City</label>
        <input type="text" value={value.city || ""} readOnly />
      </div>
      <div className="form-group">
        <label>State</label>
        <input type="text" value={value.state || ""} readOnly />
      </div>
      <div className="form-group">
        <label>Country</label>
        <input type="text" value={value.country || ""} readOnly />
      </div>
      <div className="form-group">
        <label>Pincode</label>
        <input type="text" value={value.pincode || ""} readOnly />
      </div>
    </div>
  );
}

export default GoogleAddressSearch;
